import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, RefreshCw } from "lucide-react";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { useHubToast } from "@/components/ui/HubToast";
import { Label } from "@/components/ui/Input";
import { CardTitleWithHelp, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { ZplLocationSelect } from "@/components/ZplLocationSelect";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { HUB_CACHE_CHANNEL } from "@/lib/hubCacheChannels";
import { useHubCacheStream } from "@/lib/useHubCacheStream";
import { megaventoryPurchaseOrderDocumentUrl } from "@/lib/megaventoryDocumentUrls";

import { pottingCartonPrintLabelButtonText, type PottingCartonCardModel } from "./PottingOperatorCartonsPanel";
import { ScsPottingDraftTable } from "./ScsPottingDraftTable";
import {
  PRE_ENCAP_CARTONS_QUERY_KEY,
  PRE_ENCAP_DRAFT_QUERY_KEY,
  removePreEncapDraft,
} from "./preEncapsulationApi";
import {
  createPottingSendFirstPassPo,
  createPottingSendReworkPo,
  fetchScsPottingDraftRows,
  fetchScsPottingSendDraft,
  printPottingSendFirstPassCarton,
  printPottingSendReworkCarton,
  moveVendorPottingCartonToInternal,
  POTTING_SEND_FIRST_PASS_DRAFT_KEY,
  POTTING_SEND_OPERATORS_KEY,
  POTTING_SEND_REWORK_DRAFT_KEY,
  POTTING_SEND_SCS_DRAFT_KEY,
  POTTING_SEND_SCS_DRAFT_ROWS_KEY,
  reassignPottingSendFirstPassCarton,
  reassignPottingSendReworkCarton,
  removeEmptyPottingSendFirstPassCarton,
  removeEmptyPottingSendReworkCarton,
  sealPottingSendFirstPassCarton,
  sealPottingSendReworkCarton,
  unsealPottingSendFirstPassCarton,
  unsealPottingSendReworkCarton,
  type PottingSendCartonSummary,
  type PottingSendDraftOverview,
  type PottingSendFlow,
  type PottingSendOperatorOption,
} from "./pottingSendApi";
import { usePottingZplPrinter } from "./pottingZplPrinterStorage";

type FlowCartonModel = PottingCartonCardModel & { assignedBy?: string; flow: PottingSendFlow };

function operatorOptionLabel(operator: PottingSendOperatorOption): string {
  if (operator.fullName?.trim()) {
    return `${operator.fullName.trim()} (${operator.email})`;
  }
  return operator.email;
}

function pipelineBadgeTone(afterDotCount: number): "success" | "warning" {
  return afterDotCount > 0 ? "warning" : "success";
}

function flowLabel(flow: PottingSendFlow): string {
  return flow === "first_pass" ? "First pass" : "Rework";
}

function toFlowCardModel(row: PottingSendCartonSummary): FlowCartonModel {
  return {
    flow: row.flow ?? "first_pass",
    cartonNumber: row.cartonNumber,
    laborSku: row.laborSku,
    productLabel: row.productLabel,
    sealed: row.sealed,
    sealedAt: row.sealedAt,
    labelPrintedAt: row.labelPrintedAt,
    rowCount: row.rowCount,
    assignedBy: row.assignedBy,
  };
}

function unsealedCartonsWithRows(overview: PottingSendDraftOverview | undefined): number[] {
  return (overview?.cartons ?? [])
    .filter((row) => !row.sealed && row.rowCount > 0)
    .map((row) => row.cartonNumber);
}

function canCreatePoForFlow(overview: PottingSendDraftOverview | undefined): boolean {
  if (!overview) {
    return false;
  }
  return (
    overview.openDraftRowCount > 0 &&
    overview.rowsMissingCarton === 0 &&
    unsealedCartonsWithRows(overview).length === 0 &&
    overview.activePottingSendPurchaseOrderMvNumber == null
  );
}

function poCreateHoverHint(
  flow: PottingSendFlow,
  overview: PottingSendDraftOverview | undefined,
  writesAllowed: boolean,
  busy: boolean,
): string | undefined {
  if (!writesAllowed) {
    return "Your role has read-only access to Manufacturing Management.";
  }
  const reason = poCreateTitle(flow, overview);
  if (reason != null) {
    return reason;
  }
  if (busy) {
    return "Please wait for the current action to finish.";
  }
  return undefined;
}

function poCreateTitle(flow: PottingSendFlow, overview: PottingSendDraftOverview | undefined): string | undefined {
  if (!overview) {
    return undefined;
  }
  const unsealed = unsealedCartonsWithRows(overview);
  if (overview.openDraftRowCount === 0) {
    return `No open ${flowLabel(flow).toLowerCase()} draft rows.`;
  }
  if (overview.activePottingSendPurchaseOrderMvNumber != null) {
    return `Purchase order ${overview.activePottingSendPurchaseOrderMvNumber} is already linked to this ${flowLabel(flow).toLowerCase()} batch. Refresh after processing finishes.`;
  }
  if (overview.rowsMissingCarton > 0) {
    const where = flow === "first_pass" ? "Pre-Encapsulation" : "QC Incidents";
    return `${overview.rowsMissingCarton} ${flowLabel(flow).toLowerCase()} row(s) are not in a carton yet (assign on ${where}).`;
  }
  if (unsealed.length > 0) {
    return `Seal ${flowLabel(flow).toLowerCase()} cartons before creating the purchase order: ${unsealed.join(", ")}.`;
  }
  return undefined;
}

function PottingCartonReassignModal({
  carton,
  operators,
  writesAllowed,
  busy,
  onClose,
  onConfirm,
}: {
  carton: FlowCartonModel;
  operators: PottingSendOperatorOption[];
  writesAllowed: boolean;
  busy: boolean;
  onClose: () => void;
  onConfirm: (operatorEmail: string) => void;
}) {
  const [selectedEmail, setSelectedEmail] = useState("");

  const eligibleOperators = useMemo(
    () =>
      operators.filter(
        (operator) =>
          operator.email.trim().toLowerCase() !== (carton.assignedBy ?? "").trim().toLowerCase(),
      ),
    [carton.assignedBy, operators],
  );

  useEffect(() => {
    setSelectedEmail(eligibleOperators[0]?.email ?? "");
  }, [carton.assignedBy, carton.cartonNumber, eligibleOperators]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/50 px-4 py-6">
      <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-5 shadow-lg">
        <h2 className="text-lg font-semibold text-slate-900">
          Reassign {flowLabel(carton.flow).toLowerCase()} carton {carton.cartonNumber}
        </h2>
        <p className="mt-2 text-sm text-slate-600">
          {carton.laborSku} · {carton.productLabel}
          {carton.assignedBy ? (
            <>
              {" "}
              — currently assigned to <span className="font-medium">{carton.assignedBy}</span>
            </>
          ) : null}
        </p>
        <p className="mt-2 text-sm text-slate-500">
          Only in-progress (unsealed) cartons can be reassigned. The new operator will seal and print on{" "}
          {carton.flow === "first_pass" ? "Pre-Encapsulation" : "QC Incidents"}.
        </p>
        <div className="mt-4">
          <Label htmlFor="potting-reassign-operator">Assign to operator</Label>
          <select
            id="potting-reassign-operator"
            className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm"
            value={selectedEmail}
            disabled={!writesAllowed || busy || eligibleOperators.length === 0}
            onChange={(event) => setSelectedEmail(event.target.value)}
          >
            {eligibleOperators.length === 0 ? (
              <option value="">No other operators available</option>
            ) : (
              eligibleOperators.map((operator) => (
                <option key={operator.email} value={operator.email}>
                  {operatorOptionLabel(operator)}
                </option>
              ))
            )}
          </select>
        </div>
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <Button type="button" variant="secondary" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            disabled={!writesAllowed || busy || selectedEmail.trim() === ""}
            isLoading={busy}
            onClick={() => onConfirm(selectedEmail.trim())}
          >
            Reassign carton
          </Button>
        </div>
      </div>
    </div>
  );
}

export function ScsVendorSendSection({ operators }: { operators: PottingSendOperatorOption[] }) {
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const writesAllowed = useHubModuleWrite("manufacturing_management");
  const manufacturingWritesAllowed = useHubModuleWrite("manufacturing");
  const { station, setStation, selectedPrinter, unavailableMessage, documents } = usePottingZplPrinter();
  const [printPendingKey, setPrintPendingKey] = useState<string | null>(null);
  const [sealPendingKey, setSealPendingKey] = useState<string | null>(null);
  const [unsealPendingKey, setUnsealPendingKey] = useState<string | null>(null);
  const [removePendingKey, setRemovePendingKey] = useState<string | null>(null);
  const [moveToInternalPendingKey, setMoveToInternalPendingKey] = useState<string | null>(null);
  const [reassignTarget, setReassignTarget] = useState<FlowCartonModel | null>(null);
  const [createdPoLink, setCreatedPoLink] = useState<{
    flow: PottingSendFlow;
    publicPurchaseOrderId: number;
    mvId: number | null;
    poNumber: string | null;
  } | null>(null);

  const draftQuery = useQuery({
    queryKey: POTTING_SEND_SCS_DRAFT_KEY,
    queryFn: () => fetchScsPottingSendDraft({ background: true }),
    staleTime: 30_000,
  });

  const draftRowsQuery = useQuery({
    queryKey: POTTING_SEND_SCS_DRAFT_ROWS_KEY,
    queryFn: () => fetchScsPottingDraftRows({ background: true }),
    staleTime: 30_000,
  });

  useHubCacheStream({
    channels: [
      HUB_CACHE_CHANNEL.manufacturingPottingSendScsOverview,
      HUB_CACHE_CHANNEL.manufacturingPottingSendScsDraftRows,
    ],
    onEvent: (event) => {
      if (event.phase !== "ready" && event.phase !== "invalidated") {
        return;
      }
      if (event.channel === HUB_CACHE_CHANNEL.manufacturingPottingSendScsOverview) {
        void queryClient.invalidateQueries({ queryKey: POTTING_SEND_SCS_DRAFT_KEY });
        return;
      }
      if (event.channel === HUB_CACHE_CHANNEL.manufacturingPottingSendScsDraftRows) {
        void queryClient.invalidateQueries({ queryKey: POTTING_SEND_SCS_DRAFT_ROWS_KEY });
      }
    },
  });

  const invalidateScsQueries = () => {
    void queryClient.invalidateQueries({ queryKey: POTTING_SEND_SCS_DRAFT_KEY });
    void queryClient.invalidateQueries({ queryKey: POTTING_SEND_SCS_DRAFT_ROWS_KEY });
    void queryClient.invalidateQueries({ queryKey: POTTING_SEND_FIRST_PASS_DRAFT_KEY });
    void queryClient.invalidateQueries({ queryKey: POTTING_SEND_REWORK_DRAFT_KEY });
    void queryClient.invalidateQueries({ queryKey: PRE_ENCAP_DRAFT_QUERY_KEY });
  };

  const removeDraftMutation = useMutation({
    mutationFn: (id: number) => removePreEncapDraft(id),
    onSuccess: (data) => {
      showToast({ tone: "success", message: `Removed draft row ${data.id}.` });
      invalidateScsQueries();
    },
    onError: (error: Error) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  const createFirstPassPoMutation = useMutation({
    mutationFn: createPottingSendFirstPassPo,
    onSuccess: (data) => {
      const poLabel = data.mv_purchase_order_number ?? (data.mv_purchase_order_id != null ? `MV PO ${data.mv_purchase_order_id}` : `GPO ${data.public_purchase_order_id}`);
      setCreatedPoLink({
        flow: "first_pass",
        publicPurchaseOrderId: data.public_purchase_order_id,
        mvId: data.mv_purchase_order_id,
        poNumber: data.mv_purchase_order_number,
      });
      showToast({
        tone: "success",
        message: `Created first pass potting purchase order ${poLabel}. Linked ${data.linked_potting_rows} row(s); advanced ${data.station_serial_updates} serial(s) to Potting.`,
      });
      invalidateScsQueries();
    },
    onError: (error: Error) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  const createReworkPoMutation = useMutation({
    mutationFn: createPottingSendReworkPo,
    onSuccess: (data) => {
      const poLabel = data.mv_purchase_order_number ?? (data.mv_purchase_order_id != null ? `MV PO ${data.mv_purchase_order_id}` : `GPO ${data.public_purchase_order_id}`);
      setCreatedPoLink({
        flow: "rework",
        publicPurchaseOrderId: data.public_purchase_order_id,
        mvId: data.mv_purchase_order_id,
        poNumber: data.mv_purchase_order_number,
      });
      showToast({
        tone: "success",
        message: `Created rework potting purchase order ${poLabel}. Linked ${data.linked_rework_rows} row(s); advanced ${data.station_serial_updates} serial(s) to Potting.`,
      });
      invalidateScsQueries();
    },
    onError: (error: Error) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  const printMutation = useMutation({
    mutationFn: ({ flow, cartonNumber, printer }: { flow: PottingSendFlow; cartonNumber: number; printer: string }) =>
      flow === "first_pass"
        ? printPottingSendFirstPassCarton(cartonNumber, printer)
        : printPottingSendReworkCarton(cartonNumber, printer),
    onMutate: ({ flow, cartonNumber }) => {
      setPrintPendingKey(`${flow}:${cartonNumber}`);
    },
    onSettled: () => {
      setPrintPendingKey(null);
    },
    onSuccess: (data, variables) => {
      showToast({
        tone: "success",
        message: `Sent ${flowLabel(variables.flow).toLowerCase()} carton ${variables.cartonNumber} label to the printer (${data.lineCount} line group${data.lineCount === 1 ? "" : "s"}).`,
      });
      invalidateScsQueries();
    },
    onError: (error: Error) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  const reassignMutation = useMutation({
    mutationFn: ({
      flow,
      cartonNumber,
      operatorEmail,
    }: {
      flow: PottingSendFlow;
      cartonNumber: number;
      operatorEmail: string;
    }) =>
      flow === "first_pass"
        ? reassignPottingSendFirstPassCarton(cartonNumber, operatorEmail)
        : reassignPottingSendReworkCarton(cartonNumber, operatorEmail),
    onSuccess: (data) => {
      const result = data as {
        cartonNumber: number;
        previousOperatorEmail: string;
        newOperatorEmail: string;
      };
      showToast({
        tone: "success",
        message: `Carton ${result.cartonNumber} reassigned from ${result.previousOperatorEmail} to ${result.newOperatorEmail}.`,
      });
      setReassignTarget(null);
      invalidateScsQueries();
      void queryClient.invalidateQueries({ queryKey: POTTING_SEND_OPERATORS_KEY });
    },
    onError: (error: Error) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  const removeEmptyCartonMutation = useMutation({
    mutationFn: ({ flow, cartonNumber }: { flow: PottingSendFlow; cartonNumber: number }) =>
      flow === "first_pass"
        ? removeEmptyPottingSendFirstPassCarton(cartonNumber)
        : removeEmptyPottingSendReworkCarton(cartonNumber),
    onMutate: ({ flow, cartonNumber }) => {
      setRemovePendingKey(`${flow}:${cartonNumber}`);
    },
    onSettled: () => {
      setRemovePendingKey(null);
    },
    onSuccess: (data) => {
      showToast({ tone: "success", message: `Removed empty carton ${data.cartonNumber}.` });
      invalidateScsQueries();
      void queryClient.invalidateQueries({ queryKey: POTTING_SEND_OPERATORS_KEY });
      void queryClient.invalidateQueries({ queryKey: PRE_ENCAP_CARTONS_QUERY_KEY });
    },
    onError: (error: Error) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  const unsealMutation = useMutation({
    mutationFn: ({ flow, cartonNumber }: { flow: PottingSendFlow; cartonNumber: number }) =>
      flow === "first_pass"
        ? unsealPottingSendFirstPassCarton(cartonNumber)
        : unsealPottingSendReworkCarton(cartonNumber),
    onMutate: ({ flow, cartonNumber }) => {
      setUnsealPendingKey(`${flow}:${cartonNumber}`);
    },
    onSettled: () => {
      setUnsealPendingKey(null);
    },
    onSuccess: (data, variables) => {
      showToast({
        tone: "success",
        message: `Carton ${data.cartonNumber} is unsealed. The assigned operator can add or remove units on ${variables.flow === "first_pass" ? "Pre-Encapsulation" : "QC Incidents"}.`,
      });
      invalidateScsQueries();
      void queryClient.invalidateQueries({ queryKey: POTTING_SEND_OPERATORS_KEY });
      void queryClient.invalidateQueries({ queryKey: PRE_ENCAP_CARTONS_QUERY_KEY });
    },
    onError: (error: Error) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  const moveToInternalMutation = useMutation({
    mutationFn: (cartonNumber: number) => moveVendorPottingCartonToInternal(cartonNumber),
    onMutate: (cartonNumber) => {
      setMoveToInternalPendingKey(`first_pass:${cartonNumber}`);
    },
    onSettled: () => {
      setMoveToInternalPendingKey(null);
    },
    onSuccess: (data) => {
      showToast({
        tone: "success",
        message: `Moved ${data.movedRowCount} unit${data.movedRowCount === 1 ? "" : "s"} (${data.laborSku} · ${data.productLabel}) to in-house Potting — no send carton.`,
      });
      invalidateScsQueries();
      void queryClient.invalidateQueries({ queryKey: POTTING_SEND_OPERATORS_KEY });
      void queryClient.invalidateQueries({ queryKey: PRE_ENCAP_CARTONS_QUERY_KEY });
    },
    onError: (error: Error) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  const sealMutation = useMutation({
    mutationFn: ({ flow, cartonNumber }: { flow: PottingSendFlow; cartonNumber: number }) =>
      flow === "first_pass"
        ? sealPottingSendFirstPassCarton(cartonNumber)
        : sealPottingSendReworkCarton(cartonNumber),
    onMutate: ({ flow, cartonNumber }) => {
      setSealPendingKey(`${flow}:${cartonNumber}`);
    },
    onSettled: () => {
      setSealPendingKey(null);
    },
    onSuccess: (data, variables) => {
      showToast({
        tone: "success",
        message: `Carton ${data.cartonNumber} is sealed. You can print its label here or the operator can print on ${variables.flow === "first_pass" ? "Pre-Encapsulation" : "QC Incidents"}.`,
      });
      invalidateScsQueries();
      void queryClient.invalidateQueries({ queryKey: POTTING_SEND_OPERATORS_KEY });
      void queryClient.invalidateQueries({ queryKey: PRE_ENCAP_CARTONS_QUERY_KEY });
    },
    onError: (error: Error) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  const firstPassOverview = draftQuery.data?.firstPass;
  const reworkOverview = draftQuery.data?.rework;
  const cardModels = useMemo(
    () => (draftQuery.data?.cartons ?? []).map(toFlowCardModel),
    [draftQuery.data?.cartons],
  );
  const draftRows = draftRowsQuery.data?.rows ?? [];
  const hasInvalidFi3c = draftRows.some(
    (row) =>
      row.sendFlow === "first_pass" &&
      row.rowKind === "FI-3C" &&
      !["N", "B", "Q"].includes((row.skuCoreFeature ?? "").trim().toUpperCase()),
  );
  const busy =
    createFirstPassPoMutation.isPending ||
    createReworkPoMutation.isPending ||
    printMutation.isPending ||
    removeDraftMutation.isPending ||
    reassignMutation.isPending ||
    removeEmptyCartonMutation.isPending ||
    sealMutation.isPending ||
    unsealMutation.isPending ||
    moveToInternalMutation.isPending;
  const refreshing = draftQuery.isFetching || draftRowsQuery.isFetching;
  const firstPassInDraft = draftQuery.data?.badgeCounts.firstPassInDraft ?? firstPassOverview?.openDraftRowCount ?? 0;
  const firstPassCompleteAtCalTest = draftQuery.data?.badgeCounts.firstPassCompleteAtCalTest ?? 0;
  const reworkInDraft = draftQuery.data?.badgeCounts.reworkInDraft ?? reworkOverview?.openDraftRowCount ?? 0;
  const reworkEligibleForRework =
    draftQuery.data?.badgeCounts.reworkEligibleForRework ?? reworkOverview?.rowsMissingCarton ?? 0;
  const firstPassPoDisabled = !writesAllowed || busy || !canCreatePoForFlow(firstPassOverview);
  const reworkPoDisabled = !writesAllowed || busy || !canCreatePoForFlow(reworkOverview);
  const firstPassPoHoverHint = poCreateHoverHint("first_pass", firstPassOverview, writesAllowed, busy);
  const reworkPoHoverHint = poCreateHoverHint("rework", reworkOverview, writesAllowed, busy);

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <CardTitleWithHelp hoverHint="Review open Specialty Coating Systems send cartons for first pass (Pre-Encapsulation) and rework (QC Incidents) in one place. Every draft row must be in a sealed carton before the matching purchase order create button is enabled.">
            SCS vendor send
          </CardTitleWithHelp>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={refreshing}
            onClick={() => {
              void queryClient.fetchQuery({
                queryKey: POTTING_SEND_SCS_DRAFT_KEY,
                queryFn: () => fetchScsPottingSendDraft({ refresh: true }),
              });
              void queryClient.fetchQuery({
                queryKey: POTTING_SEND_SCS_DRAFT_ROWS_KEY,
                queryFn: () => fetchScsPottingDraftRows({ refresh: true }),
              });
            }}
          >
            {refreshing ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <RefreshCw className="h-4 w-4" aria-hidden />
            )}
            Refresh
          </Button>
          <ReadOnlyHoverLayer
            active={firstPassPoDisabled && firstPassPoHoverHint != null}
            hoverHint={firstPassPoHoverHint ?? ""}
            layout="inline"
          >
            <Button
              type="button"
              size="sm"
              variant="primary"
              disabled={firstPassPoDisabled}
              isLoading={createFirstPassPoMutation.isPending}
              onClick={() => createFirstPassPoMutation.mutate()}
            >
              Create first pass PO
            </Button>
          </ReadOnlyHoverLayer>
          <ReadOnlyHoverLayer
            active={reworkPoDisabled && reworkPoHoverHint != null}
            hoverHint={reworkPoHoverHint ?? ""}
            layout="inline"
          >
            <Button
              type="button"
              size="sm"
              variant="primary"
              disabled={reworkPoDisabled}
              isLoading={createReworkPoMutation.isPending}
              onClick={() => createReworkPoMutation.mutate()}
            >
              Create rework PO
            </Button>
          </ReadOnlyHoverLayer>
        </div>
      </CardHeader>
      <CardBody className="space-y-4">
        {createdPoLink ? (
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
            <span>
              Created {flowLabel(createdPoLink.flow).toLowerCase()} purchase order{" "}
              <span className="font-medium">
                {createdPoLink.poNumber ?? (createdPoLink.mvId != null ? `MV ${createdPoLink.mvId}` : `PO ${createdPoLink.publicPurchaseOrderId}`)}
              </span>
              .
            </span>
            {createdPoLink.mvId != null ? (
              <a
                className="font-medium text-emerald-800 underline underline-offset-2 hover:text-emerald-950"
                href={megaventoryPurchaseOrderDocumentUrl(createdPoLink.mvId, createdPoLink.poNumber)}
                target="_blank"
                rel="noreferrer noopener"
              >
                Open in Megaventory
              </a>
            ) : (
              <Link
                className="font-medium text-emerald-800 underline underline-offset-2 hover:text-emerald-950"
                to={`/mrp/purchase-orders/${createdPoLink.publicPurchaseOrderId}`}
              >
                Open purchase order
              </Link>
            )}
            <button
              type="button"
              className="ml-auto text-emerald-700 hover:text-emerald-950"
              aria-label="Dismiss created purchase order notice"
              onClick={() => setCreatedPoLink(null)}
            >
              ✕
            </button>
          </div>
        ) : null}
        {firstPassOverview?.activePottingSendPurchaseOrderMvNumber ? (
          <p className="text-sm text-amber-800">
            First pass purchase order {firstPassOverview.activePottingSendPurchaseOrderMvNumber} is already linked to
            open draft rows. Wait until processing finishes, then refresh.
          </p>
        ) : null}
        {reworkOverview?.activePottingSendPurchaseOrderMvNumber ? (
          <p className="text-sm text-amber-800">
            Rework purchase order {reworkOverview.activePottingSendPurchaseOrderMvNumber} is already linked to open draft
            rows. Wait until processing finishes, then refresh.
          </p>
        ) : null}

        <div className="flex flex-wrap gap-2 text-xs text-slate-600">
          <Badge tone={pipelineBadgeTone(firstPassCompleteAtCalTest)}>
            First pass: {firstPassInDraft} in draft · {firstPassCompleteAtCalTest} complete at cal+test
          </Badge>
          <Badge tone={pipelineBadgeTone(reworkEligibleForRework)}>
            Rework: {reworkInDraft} in draft · {reworkEligibleForRework} eligible for rework
          </Badge>
        </div>

        <ZplLocationSelect
          id="scs-vendor-send-location"
          value={station}
          onChange={setStation}
          unavailableMessage={unavailableMessage}
          className="max-w-xs"
        
      documents={documents}
    />

        <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
          {cardModels.length === 0 ? (
            <p className="text-sm text-slate-500">No open send cartons.</p>
          ) : (
            cardModels.map((carton) => {
              const cartonKey = `${carton.flow}:${carton.cartonNumber}`;
              return (
                <div key={cartonKey} className="rounded-lg border border-slate-200 bg-white px-3 py-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium text-slate-900">Carton {carton.cartonNumber}</p>
                        <Badge tone={carton.flow === "first_pass" ? "info" : "warning"}>{flowLabel(carton.flow)}</Badge>
                      </div>
                      <p className="mt-0.5 font-mono text-xs text-slate-700">
                        {carton.laborSku} · {carton.productLabel}
                      </p>
                      <p className="mt-1 text-xs text-slate-500">
                        {carton.rowCount} unit{carton.rowCount === 1 ? "" : "s"}
                        {carton.assignedBy ? ` · ${carton.assignedBy}` : ""}
                      </p>
                    </div>
                    {carton.sealed ? <Badge tone="success">Sealed</Badge> : <Badge tone="warning">Unsealed</Badge>}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <ReadOnlyHoverLayer
                      active={!writesAllowed}
                      hoverHint="Your role has read-only access to Manufacturing Management."
                      layout="inline"
                    >
                      <div className="flex flex-wrap gap-2">
                        {!carton.sealed ? (
                          <>
                            <Button
                              type="button"
                              size="sm"
                              variant="primary"
                              disabled={!writesAllowed || busy || carton.rowCount < 1}
                              isLoading={sealPendingKey === cartonKey}
                              title="Mark this carton sealed so purchase order create can proceed."
                              onClick={() => sealMutation.mutate({ flow: carton.flow, cartonNumber: carton.cartonNumber })}
                            >
                              Seal carton
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              disabled={!writesAllowed || busy}
                              onClick={() => setReassignTarget(carton)}
                            >
                              Reassign
                            </Button>
                            {carton.flow === "first_pass" && carton.rowCount > 0 ? (
                              <Button
                                type="button"
                                size="sm"
                                variant="secondary"
                                disabled={!writesAllowed || busy}
                                isLoading={moveToInternalPendingKey === cartonKey}
                                title="Move every unit in this vendor carton to in-house internal potting at the Potting station (no send carton)."
                                onClick={() => moveToInternalMutation.mutate(carton.cartonNumber)}
                              >
                                Move to internal
                              </Button>
                            ) : null}
                          </>
                        ) : (
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            disabled={!writesAllowed || busy || carton.rowCount < 1}
                            isLoading={unsealPendingKey === cartonKey}
                            title="Reopen this carton so the operator can add or remove units before re-sealing."
                            onClick={() => unsealMutation.mutate({ flow: carton.flow, cartonNumber: carton.cartonNumber })}
                          >
                            Unseal
                          </Button>
                        )}
                        {carton.rowCount === 0 && !carton.sealed ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="danger"
                            disabled={!writesAllowed || busy}
                            isLoading={removePendingKey === cartonKey}
                            onClick={() =>
                              removeEmptyCartonMutation.mutate({ flow: carton.flow, cartonNumber: carton.cartonNumber })
                            }
                          >
                            Remove
                          </Button>
                        ) : null}
                      </div>
                    </ReadOnlyHoverLayer>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={busy || !carton.sealed}
                      isLoading={printPendingKey === cartonKey}
                      title={!carton.sealed ? "Operators must seal this carton before printing." : undefined}
                      onClick={() =>
                        printMutation.mutate({
                          flow: carton.flow,
                          cartonNumber: carton.cartonNumber,
                          printer: selectedPrinter.trim(),
                        })
                      }
                    >
                      {pottingCartonPrintLabelButtonText(carton)}
                    </Button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <ScsPottingDraftTable
          rows={draftRows}
          isMutating={busy}
          writesAllowed={manufacturingWritesAllowed}
          hasInvalidFi3c={hasInvalidFi3c}
          onRemoveFirstPass={(id) => removeDraftMutation.mutate(id)}
        />
      </CardBody>
      {reassignTarget != null ? (
        <PottingCartonReassignModal
          carton={reassignTarget}
          operators={operators}
          writesAllowed={writesAllowed}
          busy={reassignMutation.isPending}
          onClose={() => setReassignTarget(null)}
          onConfirm={(operatorEmail) =>
            reassignMutation.mutate({
              flow: reassignTarget.flow,
              cartonNumber: reassignTarget.cartonNumber,
              operatorEmail,
            })
          }
        />
      ) : null}
    </Card>
  );
}
